/* ============================================================================
   KITSTERS · GIFT FINDER ENGINE

   Pipeline (every stage is deterministic and explainable):

     1. PARSE     free text  → intent {interests, recipient, occasion, budget,
                               age, group size, vibes, exclusions, tokens}
                               Optional: Claude enriches the intent. It never
                               ranks and never overrides a hard rule.
     2. FILTER    hard constraints only: user exclusions, budget ceiling,
                  availability. Applied as a relaxation LADDER so the page can
                  never return an empty grid without saying why.
     3. SCORE     weighted multi-signal score per kit, every signal recorded so
                  the card can show WHY it matched.
     4. SELECT    greedy MMR - a diversity penalty stops eight near-identical
                  painting kits, and the penalty relaxes when the shopper
                  explicitly asked for one category.
     5. EXPLAIN   reasons → chips on the card; matched facets → removable pills.

   JS-off: <noscript> block in the section links out to the category pages.
   ========================================================================== */
(function () {
  "use strict";

  var REDUCED = window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---- scoring weights. One table, so tuning is a single-file change. ---- */
  var W = {
    interest:     30,   /* kit is in the interest collection the shopper named  */
    interest2:    14,   /* kit is in a secondary interest they also mentioned   */
    recipient:    24,   /* kit is merchandised for that person                  */
    occasion:     12,   /* occasion → category affinity                         */
    age:          10,   /* age band lines up                                    */
    ageMiss:     -12,   /* age band clearly does not line up                    */
    budget:       16,   /* how well the price uses the stated budget            */
    text:         18,   /* IDF-weighted overlap with title / type / details     */
    proof:         8,   /* real rating + review count                           */
    vibe:          6,   /* "quick", "beginner", "screen-free" etc.              */
    aiPick:       20,   /* Claude independently short-listed this kit           */
    soldOut:     -45    /* keep, but sink - shoppers can still hit Notify Me    */
  };

  var DIVERSITY_LOOSE = 14; /* no explicit category asked for → spread wide     */
  var DIVERSITY_TIGHT = 5;  /* they asked for art kits → give them art kits     */
  var PAGE_SIZE       = 6;
  var MAX_RESULTS     = 24;
  var BUDGET_GRACE    = 1.25;
  var MIN_MATCH       = 4;  /* above the max merchandising tie-breaker (3) */

  var STOPWORDS = (" a an and the for to of in on with my our your their his her is are am " +
    "want need looking gift gifts present something someone who whom loves love like likes " +
    "get buy good best nice under below around about budget usd dollar dollars please me i she he " +
    "they them we us it that this can you do does have has any kit kits diy give giving find " +
    "show me some what which will would could should be been being ").split(/\s+/);

  /* ---------------------------------------------------------------- language */
  var HINGLISH_PHRASES = ["ke liye", "ke lie", "ke saath", "ke andar", "se kam", "se zyada", "ke alawa"];
  var HINGLISH_WORDS = ("behen behan bhai bhaiya dadi nani chacha chachi kuch koi kaafi chahiye " +
    "chahie saal mahine bachche bachcha bacha baccha bache dost doston yaar nahi nahin thoda " +
    "zyada bahut accha acha achha theek bilkul zaroor pyaar pyar ghar mujhe humein hamein mummy " +
    "mera meri mere karna karo dikhao batao pasand khushi lena leni dena wala wali waale kitna " +
    "kitni kitne kahan kyun kaise uska uski sabke sab").split(/\s+/);

  var LANG_NAMES = {
    en: "English", hi: "Hindi", hinglish: "Hinglish", ta: "Tamil",
    gu: "Gujarati", bn: "Bengali", te: "Telugu", kn: "Kannada", ml: "Malayalam"
  };

  var STRINGS = {
    en: {
      phases: ["Reading your sentence…", "Scoring every kit on fit, person and budget…", "Ranking the best matches…"],
      matched:      function (n) { return n === 1 ? "1 kit fits" : n + " kits fit"; },
      closest:      function (n) { return n === 1 ? "1 close match" : n + " closest matches"; },
      popular:      "Our most-loved kits",
      noteOver:     "Nothing landed inside that budget, so these are the nearest kits just above it - the amount over is marked on each card.",
      noteSoldOut:  "The exact fits are out of stock right now. Tap Notify Me and we will message you the moment they are back.",
      noteLoose:    "Nothing matched every part of that, so these are the closest kits we have.",
      notePopular:  "Tell us who it is for, the vibe or a budget and we will narrow this right down.",
      empty:        "We could not find a kit for that combination.",
      emptyCta:     "Browse all DIY kits",
      matchedLead:  "We read",
      notPrefix:    "Not ",
      adding:       "Adding…",
      added:        "Added",
      best:         "Best match"
    },
    hi: {
      phases: ["आपकी बात पढ़ रहे हैं…", "हर किट को परख रहे हैं…", "सबसे बेहतरीन किट चुन रहे हैं…"],
      matched:      function (n) { return n + " किट मिले"; },
      closest:      function (n) { return n + " करीबी विकल्प"; },
      popular:      "हमारे सबसे पसंदीदा किट्स",
      noteOver:     "इस बजट में कुछ नहीं मिला - ये उससे थोड़े ऊपर हैं, हर कार्ड पर अंतर लिखा है।",
      noteSoldOut:  "जो किट सही बैठते हैं वे अभी स्टॉक में नहीं हैं। Notify Me दबाएँ, वापस आते ही बताएँगे।",
      noteLoose:    "पूरी तरह मेल नहीं खाया - ये सबसे करीबी किट हैं।",
      notePopular:  "बताइए किसके लिए चाहिए और कितना बजट है - हम सही किट ढूंढ देंगे।",
      empty:        "इस कॉम्बो के लिए कोई किट नहीं मिली।",
      emptyCta:     "सभी किट्स देखें",
      matchedLead:  "हमने समझा",
      notPrefix:    "नहीं ",
      adding:       "जोड़ रहे हैं…",
      added:        "जोड़ दिया",
      best:         "सबसे सही"
    }
  };

  function strings(lang) { return STRINGS[lang] || STRINGS.en; }

  function detectLanguage(text) {
    if (/[ऀ-ॿ]/.test(text)) return "hi";
    if (/[஀-௿]/.test(text)) return "ta";
    if (/[઀-૿]/.test(text)) return "gu";
    if (/[ঀ-৿]/.test(text)) return "bn";
    if (/[ఀ-౿]/.test(text)) return "te";
    if (/[ಀ-೿]/.test(text)) return "kn";
    if (/[ഀ-ൿ]/.test(text)) return "ml";
    var padded = " " + text.toLowerCase() + " ";
    for (var i = 0; i < HINGLISH_PHRASES.length; i++) {
      if (padded.indexOf(HINGLISH_PHRASES[i]) !== -1) return "hinglish";
    }
    for (var j = 0; j < HINGLISH_WORDS.length; j++) {
      if (padded.indexOf(" " + HINGLISH_WORDS[j] + " ") !== -1) return "hinglish";
    }
    return "en";
  }

  /* ======================================================================== */
  /*  BOOT                                                                    */
  /* ======================================================================== */
  /* The Gift Finder is split across three sibling sections - hero, search and
     results - so there is no single element that contains all of its markup.
     `document.body` is the only common ancestor, and every lookup below is by
     data-attribute, so binding at the body is exactly equivalent to the old
     per-root binding while surviving the split. */
  ready(function () {
    /* The hero's demo is deliberately bound BEFORE the engine guard below.
       `init` returns early whenever the results section or its catalog is
       missing, and the hero is its own section - gating the two together is
       what would silently freeze the hero on any page that carries it alone. */
    initDeck(document.body);

    if (!document.querySelector("[data-gf-root]")) return;
    init(document.body);
  });

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  function readJson(root, selector, fallback) {
    var el = root.querySelector(selector);
    if (!el) return fallback;
    try { return JSON.parse(el.textContent || ""); } catch (e) { return fallback; }
  }

  function init(root) {
    var cfg     = readJson(root, "[data-gf-config]", {});
    var aiCfg   = readJson(root, "[data-gf-ai-config]", null);
    var catalog = readJson(root, "[data-gf-catalog]", []);

    var tpl   = root.querySelector("[data-gf-cards]");
    var form  = root.querySelector("[data-gf-form]");
    var input = root.querySelector("#kgf-input");
    var grid  = root.querySelector("[data-gf-grid]");
    if (!form || !input || !grid || !tpl || !catalog.length) return;

    /* handle → card node, sourced once from the inert <template> */
    var cardByHandle = {};
    var cells = tpl.content.querySelectorAll("[data-gf-cell]");
    for (var c = 0; c < cells.length; c++) {
      cardByHandle[cells[c].getAttribute("data-handle")] = cells[c];
    }

    /* Give every catalog row its merchandised position and pre-split text. */
    catalog.forEach(function (p, i) {
      p.i = i;
      p.words = " " + String(p.x || "").replace(/[^a-z0-9ऀ-෿\s]/g, " ") + " ";
    });

    var idf     = buildIdf(catalog);
    var hasAI   = !!(aiCfg && aiCfg.apiKey);
    var refs    = collectRefs(root);
    var lang    = "en";
    var S       = strings("en");
    var intent  = null;
    var results = [];
    var shown   = 0;
    var lastAtc = null;

    /* Every place that fills the field programmatically (URL preset, example
       chip, starter tile, surprise-me, both clears) wrote `input.value = …`
       directly. Setting `.value` in JS never fires a real `input` event, so
       nothing that listens for one - the rotating ghost placeholder in
       initPlaceholder(), most visibly - ever finds out the field changed. The
       reported bug (surprise-me's text painting on top of the ghost phrase)
       was one symptom of this; the URL preset, example chips and starter
       tiles hit the identical gap, just less often (owner-reported cases are
       rarely the only instance of a pattern - audit every call site, not just
       the one that got noticed). Route every write through here instead of
       assigning `.value` directly, so the dispatched event reaches whichever
       listeners exist - the ghost-hider, and the native `input` handler that
       toggles the clear button - without this function needing to know what
       they are. */
    function setInputValue(v) {
      input.value = v;
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }

    wire();
    initPlaceholder(root, input, cfg);
    initReveal(root);
    initSticky(root, form, refs.sticky);
    initGiftCard(root);

    var preset = new URLSearchParams(window.location.search).get("q");
    if (preset) { setInputValue(preset); run(preset); }

    /* ---------------------------------------------------------------- wiring */
    function wire() {
      form.addEventListener("submit", function (e) { e.preventDefault(); run(input.value); });

      input.addEventListener("input", function () {
        if (refs.clear) refs.clear.hidden = !input.value;
      });
      if (refs.clear) {
        refs.clear.addEventListener("click", function () {
          setInputValue("");
          refs.clear.hidden = true;
          reset();
          input.focus();
        });
      }

      each(root, "[data-gf-example]", function (chip) {
        chip.addEventListener("click", function () {
          setInputValue(chip.textContent.trim());
          if (refs.clear) refs.clear.hidden = false;
          run(input.value);
        });
      });

      each(root, "[data-gf-starter]", function (tile) {
        tile.addEventListener("click", function () {
          var q = tile.getAttribute("data-gf-starter") || "";
          setInputValue(q);
          if (refs.clear) refs.clear.hidden = false;
          run(q);
        });
      });

      var surprise = root.querySelector("[data-gf-surprise]");
      if (surprise) {
        surprise.addEventListener("click", function () {
          var pool = cfg.surprisePrompts || [];
          if (!pool.length) return;
          var q = pool[Math.floor(Math.random() * pool.length)];
          setInputValue(q);
          if (refs.clear) refs.clear.hidden = false;
          run(q);
        });
      }

      each(root, "[data-budget]", function (btn) {
        btn.addEventListener("click", function () {
          var cap    = parseInt(btn.getAttribute("data-budget"), 10);
          var active = btn.getAttribute("aria-pressed") === "true";
          each(root, "[data-budget]", function (b) { b.setAttribute("aria-pressed", "false"); });
          if (!active) btn.setAttribute("aria-pressed", "true");
          if (!intent) return;
          intent.budget = active ? null : cap;
          intent.budgetSource = active ? null : "refine";
          rerank();
        });
      });

      /* The [data-gf-copy] handler lived here until 2026-08-03: it powered the
         WELCOME10 promo strip's copy-to-clipboard button, and the strip was
         removed on owner request. `copyText` went with it - it had no other
         caller. */

      /* Hero CTA + sticky mobile CTA. A bare `#kgf-input` anchor scrolls the
         field into view but leaves the caret nowhere, so the shopper lands on
         the one control the page exists for and still has to tap it. */
      each(root, "[data-gf-jump]", function (a) {
        a.addEventListener("click", function (e) {
          e.preventDefault();
          scrollTo(root.querySelector("#kgf-search") || input);
          setTimeout(function () {
            try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
          }, REDUCED ? 0 : 420);
        });
      });

      each(root, "[data-gf-reset]", function (b) {
        b.addEventListener("click", function () {
          setInputValue("");
          if (refs.clear) refs.clear.hidden = true;
          reset();
          /* preventScroll matters: a plain focus() scrolls the field into view
             from wherever the page currently is, which cancels the smooth
             scroll reset() just started and lands 200px past the band. */
          try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
        });
      });

      if (refs.more) refs.more.addEventListener("click", function () { paint(PAGE_SIZE); });

      initDrawer();
    }

    function reset() {
      intent = null;
      results = [];
      if (refs.results) refs.results.hidden = true;
      if (refs.starters) refs.starters.hidden = false;
      if (refs.echo) refs.echo.textContent = "";
      each(root, "[data-budget]", function (b) { b.setAttribute("aria-pressed", "false"); });
      /* Back to the search band, not the top of the page - the field is what
         they need next, and it now lives in its own section below the hero. */
      scrollTo(root.querySelector("#kgf-search") || root.querySelector("#kgf-top"));
    }

    /* ============================== SEARCH =============================== */
    function run(sentence) {
      sentence = String(sentence || "").trim();
      if (!sentence) { input.focus(); return; }

      lang = detectLanguage(sentence);
      S    = strings(lang);
      setBusy(true);
      if (refs.echo) refs.echo.textContent = "“" + sentence + "”";

      intent = parse(sentence, cfg);

      if (REDUCED) {
        finish(rank(intent, catalog, idf, cfg), sentence, false);
        return;
      }

      showThinking(0);

      if (hasAI) {
        var settled = false;
        var timer = setTimeout(function () { if (!settled) showThinking(1); }, 500);
        askClaude(sentence, catalog, aiCfg, lang, intent)
          .then(function (enriched) { settled = true; clearTimeout(timer); apply(enriched); })
          .catch(function (err) {
            settled = true;
            clearTimeout(timer);
            if (window.console) console.warn("[GiftFinder] AI unavailable, local engine only:", err && err.message);
            apply(null);
          });
      } else {
        setTimeout(function () { showThinking(1); }, 420);
        setTimeout(function () { apply(null); }, 900);
      }

      function apply(enriched) {
        if (enriched) intent = merge(intent, enriched);
        var ranked = rank(intent, catalog, idf, cfg);
        showThinking(2);
        setTimeout(function () { finish(ranked, sentence, true); }, 380);
      }
    }

    function rerank() {
      if (!intent) return;
      finish(rank(intent, catalog, idf, cfg), input.value.trim(), false);
    }

    function finish(outcome, sentence, celebrate) {
      hideThinking();
      results = outcome.items;
      renderHead(outcome);
      renderFacets();
      renderGrid();
      updateShare(sentence);
      if (refs.starters) refs.starters.hidden = true;
      if (refs.results) refs.results.hidden = false;
      if (celebrate && outcome.mode === "match" && results.length) confetti(refs.confetti);
      scrollTo(refs.results);
      if (refs.count) refs.count.focus();
      track(sentence, intent, outcome);
      setBusy(false);
    }

    /* ============================== RENDER =============================== */
    function renderHead(outcome) {
      if (refs.count) {
        if (outcome.mode === "popular")      refs.count.textContent = S.popular;
        else if (outcome.mode === "closest") refs.count.textContent = S.closest(results.length);
        else                                 refs.count.textContent = S.matched(results.length);
      }
      if (refs.note) {
        var note = "";
        if (outcome.mode === "popular")      note = S.notePopular;
        else if (outcome.relaxed === "over") note = S.noteOver;
        else if (outcome.relaxed === "stock")note = S.noteSoldOut;
        else if (outcome.relaxed === "loose")note = S.noteLoose;
        refs.note.textContent = note;
        refs.note.hidden = !note;
      }
    }

    function renderFacets() {
      var box = refs.facets;
      if (!box) return;
      box.textContent = "";
      if (!intent) return;

      var chips = [];
      (intent.interests || []).slice(0, 2).forEach(function (h) {
        chips.push({ key: "interest", value: h, text: label(cfg, h) });
      });
      if (intent.recipient) chips.push({ key: "recipient", value: intent.recipient, text: label(cfg, intent.recipient) });
      if (intent.occasion && cfg.occasions && cfg.occasions[intent.occasion]) {
        chips.push({ key: "occasion", value: intent.occasion, text: cfg.occasions[intent.occasion].label });
      }
      /* No age chip. Age stopped influencing the ranking when the
         "for-families" recipient was removed (2026-08-04), and a removable
         filter chip for something that filters nothing claims a match the
         engine did not make - which is exactly what this page's "every result
         says why it matched" promise forbids. Still parsed, still stripped
         from the keyword text, just never shown as a reason. */
      if (intent.budget) chips.push({ key: "budget", value: intent.budget, text: "Under " + money(intent.budget) });
      if (intent.groupSize) chips.push({ key: "groupSize", value: intent.groupSize, text: intent.groupSize + " people" });
      (intent.excluded || []).forEach(function (h) {
        chips.push({ key: "excluded", value: h, text: S.notPrefix + label(cfg, h), negative: true });
      });
      if (!chips.length) return;

      var lead = document.createElement("span");
      lead.className = "kgf-facets__lead";
      lead.textContent = S.matchedLead;
      box.appendChild(lead);

      chips.forEach(function (chip) {
        var el = document.createElement("span");
        el.className = "kgf-facet" + (chip.negative ? " kgf-facet--excluded" : "");
        el.appendChild(document.createTextNode(chip.text));

        var x = document.createElement("button");
        x.type = "button";
        x.setAttribute("aria-label", "Remove " + chip.text + " and search again");
        x.textContent = "✕";
        x.addEventListener("click", function () { dropFacet(chip); });
        el.appendChild(x);
        box.appendChild(el);
      });
    }

    function dropFacet(chip) {
      if (chip.key === "interest") {
        intent.interests = (intent.interests || []).filter(function (h) { return h !== chip.value; });
      } else if (chip.key === "excluded") {
        intent.excluded = (intent.excluded || []).filter(function (h) { return h !== chip.value; });
      } else {
        intent[chip.key] = null;
      }
      rerank();
    }

    function renderGrid() {
      grid.textContent = "";
      shown = 0;
      if (!results.length) {
        var empty = document.createElement("div");
        empty.className = "kgf-empty";
        var p = document.createElement("p");
        p.textContent = S.empty;
        var a = document.createElement("a");
        a.className = "kgf-btn kgf-btn--primary";
        a.href = "/collections/shop-all-diy-kits";
        a.textContent = S.emptyCta;
        empty.appendChild(p);
        empty.appendChild(a);
        grid.appendChild(empty);
        if (refs.moreArea) refs.moreArea.hidden = true;
        return;
      }
      paint(PAGE_SIZE);
    }

    function paint(n) {
      var batch = results.slice(shown, shown + n);
      batch.forEach(function (hit, i) {
        var source = cardByHandle[hit.p.h];
        if (!source) return;
        var cell = source.cloneNode(true);
        decorate(cell, hit, shown + i === 0);
        grid.appendChild(cell);
      });
      shown += batch.length;

      each(grid, "[data-add-to-cart]:not([data-gf-wired])", function (btn) {
        btn.setAttribute("data-gf-wired", "1");
        btn.addEventListener("click", addToCart);
      });

      if (refs.moreArea) refs.moreArea.hidden = results.length === 0;
      if (refs.more) refs.more.hidden = shown >= results.length;
    }

    /* Reason chips + urgency + the single best-match accent. */
    function decorate(cell, hit, isTop) {
      var body = cell.querySelector(".product__body");
      var name = cell.querySelector(".product__name");
      if (!body) return;

      if (isTop && hit.score > 0) {
        cell.classList.add("kgf-cell--top");
        var ribbon = document.createElement("span");
        ribbon.className = "kgf-best";
        ribbon.textContent = S.best;
        cell.appendChild(ribbon);
      }

      var reasons = hit.reasons.slice(0, 3);
      if (reasons.length) {
        var list = document.createElement("ul");
        list.className = "kgf-why";
        reasons.forEach(function (r) {
          var li = document.createElement("li");
          if (r.warn) li.className = "is-warn";
          li.textContent = r.text;
          list.appendChild(li);
        });
        body.insertBefore(list, name || body.firstChild);
      }

      var cutoff = shipsToday();
      if (cutoff) {
        var addBtn = cell.querySelector(".product__add");
        if (addBtn) {
          var urgency = document.createElement("p");
          urgency.className = "kgf-ships";
          urgency.textContent = cutoff;
          addBtn.parentNode.insertBefore(urgency, addBtn);
        }
      }
    }

    /* ---------------------------------------------------------- thinking UI */
    function showThinking(step) {
      if (!refs.thinking) return;
      refs.thinking.hidden = false;
      var pct = [22, 62, 96][step] || 0;
      if (refs.status) refs.status.textContent = S.phases[step] || S.phases[0];
      if (refs.barFill) refs.barFill.style.width = pct + "%";
      if (refs.bar) refs.bar.setAttribute("aria-valuenow", String(pct));
    }
    function hideThinking() {
      if (!refs.thinking) return;
      refs.thinking.hidden = true;
      if (refs.barFill) refs.barFill.style.width = "0%";
    }
    function setBusy(on) {
      if (!refs.go) return;
      refs.go.disabled = on;
      if (refs.goIcon) refs.goIcon.hidden = on;
      if (refs.goSpinner) refs.goSpinner.hidden = !on;
    }

    /* ---------------------------------------------------------------- cart */
    function addToCart(e) {
      var btn = e.currentTarget;
      var variant = btn.getAttribute("data-variant-id");
      if (!variant) return;
      lastAtc = btn;
      btn.setAttribute("data-gf-label", btn.innerHTML);
      btn.disabled = true;
      btn.textContent = S.adding;

      /* KitstersCart re-renders and opens Dawn's <cart-drawer>. This used to
         POST directly and dispatch `cart:refresh`, which has no listener in
         this tree, so the cart drawer stayed stale until the next page load.
         `openDrawer` below is the gift finder's OWN note panel, not the cart --
         it is unrelated and stays. */
      window.KitstersCart.addToCart({ id: parseInt(variant, 10), quantity: 1 })
        .then(function () {
          btn.textContent = S.added;
          setTimeout(openDrawer, 700);
        })
        .catch(function () { restoreAtc(); });
    }

    function restoreAtc() {
      if (!lastAtc) return;
      lastAtc.innerHTML = lastAtc.getAttribute("data-gf-label") || "Add to Cart";
      lastAtc.disabled = false;
      lastAtc = null;
    }

    /* -------------------------------------------------------------- drawer */
    function openDrawer() {
      if (!refs.drawer) { restoreAtc(); return; }
      var note = refs.drawer.querySelector("[data-gf-note]");
      var cnt  = refs.drawer.querySelector("[data-gf-note-count]");
      if (note) note.value = "";
      if (cnt) cnt.textContent = "0";
      document.body.style.overflow = "hidden";
      refs.drawer.hidden = false;
      requestAnimationFrame(function () { refs.drawer.classList.add("is-open"); });
      setTimeout(function () { if (note) note.focus(); }, 320);
    }

    function closeDrawer() {
      if (!refs.drawer) return;
      refs.drawer.classList.remove("is-open");
      document.body.style.overflow = "";
      setTimeout(function () {
        if (!refs.drawer.classList.contains("is-open")) refs.drawer.hidden = true;
      }, 400);
      restoreAtc();
    }

    function initDrawer() {
      if (!refs.drawer) return;
      each(refs.drawer, "[data-gf-drawer-close]", function (el) {
        el.addEventListener("click", closeDrawer);
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !refs.drawer.hidden) closeDrawer();
      });

      var note = refs.drawer.querySelector("[data-gf-note]");
      var cnt  = refs.drawer.querySelector("[data-gf-note-count]");
      if (note && cnt) {
        note.addEventListener("input", function () { cnt.textContent = String(note.value.length); });
      }

      var skip = refs.drawer.querySelector("[data-gf-drawer-skip]");
      if (skip) skip.addEventListener("click", function () { window.location.href = "/cart"; });

      var save = refs.drawer.querySelector("[data-gf-drawer-save]");
      if (save) {
        save.addEventListener("click", function () {
          save.disabled = true;
          /* The gift-wrap toggle that used to write a cart attribute here was
             removed 2026-08-04 - it only ever recorded a note, never added the
             real priced product, so it promised free wrapping nothing billed
             for. The real gift-wrap upsell already lives on /cart, which
             this drawer redirects to on save. */
          var body = { attributes: {} };
          var text = note ? note.value.trim() : "";
          if (text) body.note = text;
          fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
          })
            .then(function () { window.location.href = "/cart"; })
            .catch(function () { window.location.href = "/cart"; });
        });
      }
    }

    /* --------------------------------------------------------------- share */
    function updateShare(sentence) {
      if (!refs.share) return;
      var url  = window.location.origin + window.location.pathname + "?q=" + encodeURIComponent(sentence);
      var trim = sentence.length > 60 ? sentence.slice(0, 57) + "…" : sentence;
      var subject = "DIY gift kits on Kitsters for “" + trim + "”";
      refs.share.href = "mailto:?subject=" + encodeURIComponent(subject) +
                        "&body=" + encodeURIComponent(url);
    }
  } /* end init */

  /* ======================================================================== */
  /*  1 · PARSE - free text → intent                                          */
  /* ======================================================================== */
  function parse(raw, cfg) {
    var clean  = normalise(raw);
    var text   = " " + clean.toLowerCase() + " ";
    var group  = groupSize(text);
    var budget = budgetOf(text, group);

    var intent = {
      raw:          raw,
      interests:    rankDict(text, cfg.interest || {}),
      recipient:    null,
      occasion:     occasionOf(text, cfg),
      budget:       budget.value,
      budgetSource: budget.source,
      age:          ageOf(text),
      groupSize:    group,
      vibes:        rankDict(text, cfg.vibes || {}),
      excluded:     exclusionsOf(text, cfg),
      tokens:       tokenise(clean)
    };

    var recipient = recipientOf(text, cfg);
    intent.recipient = recipient;

    /* The age → recipient rule used to send anything under 15 to
       "for-families". That recipient no longer exists (owner, 2026-08-04:
       "We are not selling any kit for kids"), so a stated age no longer
       claims the recipient slot at all. */
    /* A big group with nothing else claiming the recipient slot reads as a group
       activity. This used to assign "corporate", an India handle with no US
       collection behind it, so the rule silently did nothing. There is no US
       corporate collection to point at either - the B2B line is Magic Hour, which
       is deliberately hidden from the catalogue (kt_hidden_vendor) - so a large
       group goes to the paint-party collection, which is what it actually wants. */
    if (!intent.recipient && intent.groupSize && intent.groupSize >= 8) intent.recipient = "paint-party-kits";

    /* Never chase a category the shopper explicitly rejected. */
    intent.interests = intent.interests.filter(function (h) {
      return intent.excluded.indexOf(h) === -1;
    });
    if (intent.recipient && intent.excluded.indexOf(intent.recipient) !== -1) intent.recipient = null;

    return intent;
  }

  /* Word-numbers, Hinglish amounts and vague budget words → real numbers. */
  function normalise(raw) {
    var t = String(raw || "").trim();
    var map = [
      [/\bfive\s+hundred\b/gi, "500"], [/\beight\s+hundred\b/gi, "800"],
      [/\bone\s+thousand\b/gi, "1000"], [/\bfifteen\s+hundred\b/gi, "1500"],
      [/\btwo\s+thousand\b/gi, "2000"], [/\bthree\s+thousand\b/gi, "3000"],
      [/\bfive\s+thousand\b/gi, "5000"],
      [/\bdo\s+sau\b/gi, "200"], [/\bteen\s+sau\b/gi, "300"], [/\bchar\s+sau\b/gi, "400"],
      [/\bpaanch\s+sau\b/gi, "500"], [/\bsaat\s+sau\b/gi, "700"], [/\baath\s+sau\b/gi, "800"],
      [/\bek\s+haz(?:aa|a)r\b/gi, "1000"], [/\bdo\s+haz(?:aa|a)r\b/gi, "2000"],
      [/\bteen\s+haz(?:aa|a)r\b/gi, "3000"], [/\bpaanch\s+haz(?:aa|a)r\b/gi, "5000"],
      [/\bbudget[\s-]friendly\b/gi, "under 700"], [/\baffordable\b/gi, "under 1000"],
      [/\bcheap\b/gi, "under 700"]
    ];
    map.forEach(function (pair) { t = t.replace(pair[0], pair[1]); });
    if (t.length > 500) t = t.slice(0, 497) + "…";
    return t;
  }

  /* "for 20 employees", "team of 15" - used for per-head budget maths. */
  function groupSize(text) {
    var m = text.match(/\bfor\s+(\d{1,5})\s*(?:people|persons?|employees?|guests?|members?|heads?|pax|kids?|children|colleagues?|staff|friends?|students?)\b/) ||
            text.match(/\b(\d{1,5})\s*(?:employees?|guests?|members?|heads?|pax|colleagues?|staff)\b/) ||
            text.match(/\bteam\s+of\s+(\d{1,5})\b/);
    if (!m) return null;
    var n = parseInt(m[1], 10);
    return (n >= 2 && n <= 10000) ? n : null;
  }

  /* Returns a PER-KIT ceiling. A total budget across N people is divided down. */
  function budgetOf(text, group) {
    var stripped = text
      .replace(/\b\d[\d,]*\s*(kits?|units?|pcs?|pieces?|people|persons?|pax|employees?|members?|guests?|sets?|boxes|box|heads?|colleagues?|staff|students?|children|kids?)\b/g, " ")
      .replace(/\b(\d{1,2})\s*(?:year|years|yr|yrs|yo)s?\b/g, " ");

    var candidates = [];
    var m;

    /* ⚠️ THE NUMBERS BELOW ARE PART OF THE LOCALISATION, NOT JUST THE SYMBOL.
       Every floor and ceiling in this parser was scaled to rupees: the old
       `>= 50` filter is a sensible "that is not a budget" guard when the unit
       is a rupee and a bug when it is a dollar, because it would have silently
       discarded every "under $25" the chips themselves offer. Swapping ₹ for $
       and leaving these would have produced a parser that reads the sentence,
       finds the budget, and throws it away. */
    var MIN_BUDGET = 5;
    var MAX_BUDGET = 100000;

    var range = stripped.match(/(?:\$|usd|dollars?)?\s*(\d[\d,]*)\s*(?:-|-|-|to)\s*(?:\$|usd|dollars?)?\s*(\d[\d,]*)/i);
    if (range) {
      var high = toNumber(range[2]);
      if (high >= MIN_BUDGET) candidates.push(high);
    }

    var currency = /(?:\$|usd|dollars?)\s*(\d[\d,]*\.?\d*)\s*(k)?/ig;
    while ((m = currency.exec(stripped))) candidates.push(toNumber(m[1]) * (m[2] ? 1000 : 1));

    var qualifier = /(under|below|within|upto|up\s?to|less\s+than|max(?:imum)?|around|about|budget(?:\s*of)?|approx|near)\s*(?:\$|usd|dollars?)?\s*(\d[\d,]*\.?\d*)\s*(k)?/ig;
    while ((m = qualifier.exec(stripped))) candidates.push(toNumber(m[2]) * (m[3] ? 1000 : 1));

    var shorthand = /\b(\d[\d.]*)\s*k\b/ig;
    while ((m = shorthand.exec(stripped))) candidates.push(toNumber(m[1]) * 1000);

    candidates = candidates.filter(function (x) { return x >= MIN_BUDGET && x <= MAX_BUDGET; });
    if (!candidates.length) return { value: null, source: null };

    var value = Math.max.apply(null, candidates);

    /* "$20 each for 20" is per head; "$400 for 20 people" is a total. */
    if (group) {
      var perHead = /\b(per\s+(?:head|person|kit|piece|unit|child|kid|employee)|each|apiece|a\s+head)\b/i.test(text);
      if (!perHead && value / group >= MIN_BUDGET) {
        return { value: Math.round(value / group), source: "per-person" };
      }
      return { value: Math.round(value), source: perHead ? "per-person" : "explicit" };
    }
    return { value: Math.round(value), source: "explicit" };
  }

  /* Handles "9 year old", "9-yr-old", "aged 9", "9 saal" - the hyphen form is
     how people actually type it, so [\s-]* rather than \s*. */
  function ageOf(text) {
    var m = text.match(/\b(\d{1,2})[\s-]*(?:year|years|yr|yrs|yo)s?\b/) ||
            text.match(/\baged?\s+(\d{1,2})\b/) ||
            text.match(/\b(\d{1,2})\s*saal\b/);
    if (!m) return null;
    var age = parseInt(m[1], 10);
    return (age >= 1 && age <= 99) ? age : null;
  }

  function occasionOf(text, cfg) {
    var table = cfg.occasions || {};
    for (var key in table) {
      if (!Object.prototype.hasOwnProperty.call(table, key)) continue;
      var words = table[key].words || [];
      for (var i = 0; i < words.length; i++) {
        if (hasPhrase(text, words[i])) return key;
      }
    }
    return null;
  }

  /* Every dictionary entry the text hits, strongest first. */
  function rankDict(text, dict) {
    var scored = [];
    for (var handle in dict) {
      if (!Object.prototype.hasOwnProperty.call(dict, handle)) continue;
      var phrases = dict[handle], hits = 0, specificity = 0;
      for (var i = 0; i < phrases.length; i++) {
        if (hasPhrase(text, phrases[i])) {
          hits++;
          specificity = Math.max(specificity, phrases[i].length);
        }
      }
      if (hits) scored.push({ handle: handle, hits: hits, specificity: specificity });
    }
    scored.sort(function (a, b) { return b.hits - a.hits || b.specificity - a.specificity; });
    return scored.map(function (s) { return s.handle; });
  }

  /* A phrase like "sister" beats a weak one like "her". */
  function recipientOf(text, cfg) {
    var dict = cfg.recipient || {};
    var weak = cfg.weakRecipient || [];
    var strong = null, strongLen = 0, fallback = null;
    for (var handle in dict) {
      if (!Object.prototype.hasOwnProperty.call(dict, handle)) continue;
      var phrases = dict[handle];
      for (var i = 0; i < phrases.length; i++) {
        if (!hasPhrase(text, phrases[i])) continue;
        if (weak.indexOf(phrases[i]) !== -1) {
          if (!fallback) fallback = handle;
        } else if (phrases[i].length > strongLen) {
          strong = handle;
          strongLen = phrases[i].length;
        }
      }
    }
    return strong || fallback || null;
  }

  /* Only ever fires on an explicit NEGATIVE. "she loves cooking" must not
     exclude food - that bug is why this is a whitelist of negation patterns. */
  function exclusionsOf(text, cfg) {
    var boundary = "(?=\\s*(?:kits?|related|things?|activities?|stuff|please|thanks|but\\b|,|\\.|!|\\?|\\band\\b|$))";
    var patterns = [
      "\\bno\\s+([\\w][\\w\\s]{0,30}?)",
      "\\bnot\\s+(?:any\\s+|a\\s+fan\\s+of\\s+|into\\s+|interested\\s+in\\s+)?([\\w][\\w\\s]{0,30}?)",
      "\\bwithout\\s+(?:any\\s+)?([\\w][\\w\\s]{0,25}?)",
      "\\bskip\\s+(?:the\\s+)?([\\w][\\w\\s]{0,30}?)",
      "\\bavoid\\s+(?:any\\s+)?([\\w][\\w\\s]{0,30}?)",
      "\\bdon'?t\\s+(?:want|show|include|like|prefer)\\s+(?:any\\s+)?([\\w][\\w\\s]{0,30}?)",
      "\\bexcept\\s+(?:for\\s+)?([\\w][\\w\\s]{0,30}?)",
      "\\b(?:hate|hates|hated|can'?t\\s+stand|despise[sd]?)\\s+(?:any\\s+)?([\\w][\\w\\s]{0,30}?)",
      "\\bdislike[sd]?\\s+(?:any\\s+)?([\\w][\\w\\s]{0,30}?)",
      "\\ballergic\\s+to\\s+([\\w][\\w\\s]{0,25}?)",
      "\\bstay\\s+away\\s+from\\s+(?:any\\s+)?([\\w][\\w\\s]{0,30}?)"
    ];

    /* phrase → collection handle, built from the same vocab the scorer uses */
    var lookup = {};
    ["interest", "recipient"].forEach(function (group) {
      var dict = cfg[group] || {};
      for (var handle in dict) {
        if (!Object.prototype.hasOwnProperty.call(dict, handle)) continue;
        dict[handle].forEach(function (phrase) { lookup[phrase] = handle; });
      }
    });

    var excluded = [];
    patterns.forEach(function (source) {
      var re = new RegExp(source + boundary, "gi");
      var m;
      while ((m = re.exec(text)) !== null) {
        var phrase = m[1].trim().toLowerCase().replace(/\s+/g, " ");
        if (!phrase) continue;
        for (var key in lookup) {
          if (!Object.prototype.hasOwnProperty.call(lookup, key)) continue;
          var hit = phrase === key ||
                    (key.length > 3 && phrase.indexOf(key) !== -1) ||
                    (phrase.length > 3 && key.indexOf(phrase) !== -1);
          if (hit && excluded.indexOf(lookup[key]) === -1) excluded.push(lookup[key]);
        }
      }
    });
    return excluded;
  }

  function tokenise(raw) {
    var parts = String(raw).toLowerCase().replace(/[^a-z0-9ऀ-෿\s]/g, " ").split(/\s+/);
    var out = [], seen = {};
    parts.forEach(function (t) {
      if (t.length < 3) return;
      if (STOPWORDS.indexOf(t) !== -1) return;
      if (/^\d+$/.test(t)) return;
      if (seen[t]) return;
      seen[t] = 1;
      out.push(t);
    });
    return out;
  }

  /* ======================================================================== */
  /*  2-4 · FILTER → SCORE → SELECT                                           */
  /* ======================================================================== */

  /* Inverse document frequency, so "kit" counts for nothing and "terrarium"
     counts for a lot. Computed once per page load over the whole catalog. */
  function buildIdf(catalog) {
    var df = {}, n = catalog.length;
    catalog.forEach(function (p) {
      var seen = {};
      p.words.split(/\s+/).forEach(function (w) {
        if (w.length < 3 || seen[w]) return;
        seen[w] = 1;
        df[w] = (df[w] || 0) + 1;
      });
    });
    return function (token) {
      var d = df[token] || 0;
      return Math.log((n + 1) / (d + 1));
    };
  }

  function rank(intent, catalog, idf, cfg) {
    var size = Math.max(1, catalog.length);

    /* A "structural" signal is something we can actually filter on. Free text
       alone ("show me something nice") is not one. */
    var structural = !!(intent.interests.length || intent.recipient || intent.occasion ||
                        intent.budget || intent.groupSize);

    /* Fallback shelf. Still honours exclusions - "no food" means no food even
       when we have nothing better to offer. */
    function popular() {
      var base = catalog.filter(function (p) { return !isExcluded(p, intent.excluded); });
      if (!base.length) base = catalog;
      var inStock = base.filter(function (p) { return p.a !== false; });
      return {
        items: select(scoreAll(intent, inStock.length ? inStock : base, idf, cfg, size), intent, PAGE_SIZE * 2),
        mode: "popular",
        relaxed: null
      };
    }

    if (!structural && !intent.tokens.length) return popular();

    /* Relaxation ladder - each rung says exactly what it gave up, so the copy
       above the grid can stay honest instead of pretending it found a fit. */
    var ladder = [
      { relaxed: null,    mode: "match",   budget: 1,            stock: true },
      { relaxed: "over",  mode: "closest", budget: BUDGET_GRACE, stock: true },
      { relaxed: "stock", mode: "closest", budget: BUDGET_GRACE, stock: false },
      { relaxed: "loose", mode: "closest", budget: Infinity,     stock: false }
    ];

    for (var i = 0; i < ladder.length; i++) {
      var rung = ladder[i];
      var pool = catalog.filter(function (p) {
        if (isExcluded(p, intent.excluded)) return false;              /* never relaxed */
        if (rung.stock && p.a === false) return false;
        if (intent.budget && rung.budget !== Infinity && p.p > intent.budget * rung.budget) return false;
        return true;
      });
      if (!pool.length) continue;

      var scored = scoreAll(intent, pool, idf, cfg, size);

      /* `relevance` is the query-derived part of the score only. Star ratings
         and merchandised position order the list but must never be the reason
         we call something a match - otherwise every kit "matches" everything. */
      var best = 0;
      scored.forEach(function (hit) { if (hit.relevance > best) best = hit.relevance; });
      if (best < MIN_MATCH) continue;

      /* Cut the long tail: a kit scoring a fraction of the leader is not a
         match, and counting it would overstate what we actually found. */
      var floor = Math.max(MIN_MATCH, best * 0.35);
      var kept = scored.filter(function (hit) { return hit.relevance >= floor; });
      if (!kept.length) kept = scored.slice(0, PAGE_SIZE);

      /* On the last rung the budget is what we actually dropped, so say that
         rather than the vaguer "nothing matched" line. */
      var relaxed = rung.relaxed;
      if (relaxed === "loose" && intent.budget) relaxed = "over";

      return { items: select(kept, intent, MAX_RESULTS), mode: rung.mode, relaxed: relaxed };
    }

    return popular();
  }

  function scoreAll(intent, pool, idf, cfg, catalogSize) {
    var occasion = (cfg.occasions && intent.occasion) ? cfg.occasions[intent.occasion] : null;
    var boosts   = (occasion && occasion.boost) || null;

    /* Total IDF mass of the query, so text relevance normalises to 0..1. */
    var mass = 0;
    intent.tokens.forEach(function (t) { mass += idf(t); });

    var scored = pool.map(function (p) {
      /* rel   = signals derived from what the shopper actually said.
         extra = everything else (proof, merchandising, stock). Only `rel`
                 decides whether something counts as a match; `extra` only
                 orders kits that are already relevant. */
      var rel = 0;
      var extra = 0;
      var reasons = [];

      /* --- interest --- */
      var primary = intent.interests[0];
      if (primary && p.c.indexOf(primary) !== -1) {
        rel += W.interest;
        reasons.push({ text: label(cfg, primary), rank: 1 });
      }
      for (var k = 1; k < intent.interests.length; k++) {
        if (p.c.indexOf(intent.interests[k]) !== -1) {
          rel += W.interest2;
          reasons.push({ text: label(cfg, intent.interests[k]), rank: 4 });
          break;
        }
      }

      /* --- recipient --- */
      if (intent.recipient && p.c.indexOf(intent.recipient) !== -1) {
        rel += W.recipient;
        reasons.push({ text: label(cfg, intent.recipient), rank: 2 });
      }

      /* --- occasion --- */
      if (boosts) {
        for (var handle in boosts) {
          if (!Object.prototype.hasOwnProperty.call(boosts, handle)) continue;
          if (p.c.indexOf(handle) !== -1) {
            rel += (W.occasion * boosts[handle]) / 3;
            reasons.push({ text: occasion.label, rank: 3 });
            break;
          }
        }
      }

      /* --- age ---
         Removed 2026-08-04. Every branch here scored membership of
         "for-families", which is no longer a recipient the finder matches on,
         so the whole block could only ever add 0. A stated age is still parsed
         (it is used for group size and stripped from the keyword text); it
         simply no longer moves a product's rank. */

      /* --- budget fit: reward using the budget well, not just being cheap --- */
      if (intent.budget) {
        var ratio = p.p / intent.budget;
        if (ratio > 1) {
          var over = Math.round(p.p - intent.budget);
          rel -= Math.min(30, over / 20);
          reasons.push({ text: money(over) + " over budget", rank: 0, warn: true });
        } else if (ratio >= 0.55) {
          rel += W.budget;
          reasons.push({ text: "Fits " + money(intent.budget), rank: 5 });
        } else if (ratio >= 0.3) {
          rel += W.budget * 0.75;
          reasons.push({ text: "Well under budget", rank: 5 });
        } else {
          rel += W.budget * 0.4;
        }
      }

      /* --- text relevance (IDF-weighted) --- */
      if (mass > 0) {
        var matched = 0;
        intent.tokens.forEach(function (t) {
          if (p.words.indexOf(" " + t) !== -1) matched += idf(t);
        });
        if (matched > 0) rel += W.text * Math.min(1, matched / mass);
      }

      /* --- vibe words --- */
      if (intent.vibes.length) {
        var vibeDict = cfg.vibes || {};
        for (var v = 0; v < intent.vibes.length; v++) {
          var phrases = vibeDict[intent.vibes[v]] || [];
          var hit = phrases.some(function (phrase) { return p.words.indexOf(" " + phrase) !== -1; });
          if (hit) { rel += W.vibe; break; }
        }
      }

      /* --- Claude's short list --- */
      if (intent.aiPicks && intent.aiPicks[p.h]) {
        rel += W.aiPick;
        if (intent.aiPicks[p.h].length > 1) {
          reasons.push({ text: intent.aiPicks[p.h], rank: 0 });
        }
      }

      /* --- real social proof (ordering only, never a match reason) --- */
      var rating = parseFloat(p.r) || 0;
      var count  = parseInt(p.n, 10) || 0;
      if (rating > 0 && count > 0) {
        var quality = Math.max(0, Math.min(1, (rating - 3.5) / 1.5));
        var volume  = Math.min(1, count / 40);
        extra += W.proof * (quality * 0.65 + volume * 0.35);
        if (rating >= 4.5 && count >= 10) {
          reasons.push({ text: rating.toFixed(1) + "★ (" + count + ")", rank: 6 });
        }
      }

      /* --- merchandised order breaks ties without dominating --- */
      extra += 3 * Math.max(0, 1 - (p.i / catalogSize));

      if (p.a === false) extra += W.soldOut;

      reasons.sort(function (a, b) { return a.rank - b.rank; });
      return {
        p: p,
        relevance: rel,
        score: rel + extra,
        reasons: reasons,
        primary: primaryCategory(p, cfg)
      };
    });

    scored.sort(function (a, b) { return b.score - a.score || a.p.i - b.p.i; });
    return scored;
  }

  /* Greedy MMR: take the best, then keep taking the best-once-penalised-for-
     looking-like-what-we-already-took. Penalty relaxes when the shopper asked
     for a specific category - then repetition is the point. */
  function select(scored, intent, limit) {
    var lambda = intent.interests.length ? DIVERSITY_TIGHT : DIVERSITY_LOOSE;
    var taken = [], usedCategory = {}, remaining = scored.slice(0, 80);

    while (taken.length < limit && remaining.length) {
      var bestIndex = 0, bestValue = -Infinity;
      for (var i = 0; i < remaining.length; i++) {
        var item = remaining[i];
        var seen = usedCategory[item.primary] || 0;
        var value = item.score - lambda * seen;
        if (value > bestValue) { bestValue = value; bestIndex = i; }
      }
      var pick = remaining.splice(bestIndex, 1)[0];
      usedCategory[pick.primary] = (usedCategory[pick.primary] || 0) + 1;
      taken.push(pick);
    }
    return taken;
  }

  function primaryCategory(p, cfg) {
    var interests = cfg.interest || {};
    for (var i = 0; i < p.c.length; i++) {
      if (Object.prototype.hasOwnProperty.call(interests, p.c[i])) return p.c[i];
    }
    return p.c[0] || "other";
  }

  function isExcluded(p, excluded) {
    if (!excluded || !excluded.length) return false;
    for (var i = 0; i < excluded.length; i++) {
      if (p.c.indexOf(excluded[i]) !== -1) return true;
    }
    return false;
  }

  /* ======================================================================== */
  /*  AI - intent enrichment only. It never ranks, never overrides a filter.   */
  /* ======================================================================== */
  function askClaude(query, catalog, aiCfg, lang, localIntent) {
    var lines = catalog.slice(0, 200).map(function (p) {
      return p.h + " | " + p.t + " | " + window.KitstersMoney.formatMajor(Math.round(p.p)) + " | " + p.c.join(",");
    }).join("\n");

    var system = [
      "You read a gift shopper's sentence for Kitsters, a DIY-kit brand, and return STRUCTURED INTENT.",
      "You do NOT rank and you do NOT decide what is shown - a local scoring engine does that and enforces budget,",
      "stock and exclusion rules. Your job is comprehension: what did this person actually mean?",
      "",
      "COLLECTIONS you may use:",
      "  interests: diy-arts-and-crafts-kits | food | home-crafts | gardening | lifestyle",
      "  recipients: for-couples | for-her | for-mom | for-you | corporate",
      "",
      "EXCLUSIONS: only list a collection in excluded[] when the shopper clearly rejects it",
      "  (\"no food\", \"she hates gardening\", \"allergic to nuts\"). A positive mention",
      "  (\"she loves cooking\") is NEVER an exclusion.",
      "",
      "BUDGET: return the per-kit ceiling as a number in the store currency. If they gave a total for N people, divide it.",
      "PICKS: optionally short-list up to 8 handles that exist in the catalog, each with a reason of 4 words or fewer.",
      "  Never invent a handle. Picks are a hint that gets scored, not a final answer.",
      "",
      "Reply with JSON only. No markdown fence, no text outside the JSON."
    ].join("\n");

    if (lang && lang !== "en") {
      system += "\n\nLANGUAGE: the shopper wrote in " + (LANG_NAMES[lang] || lang) +
        (lang === "hinglish" ? " (Hindi typed in Roman letters)." : ".") +
        " Understand it fully and write every 'reason' in that same language.";
    }

    var user =
      "Shopper said: \"" + query + "\"\n\n" +
      "Local parser already read: " + JSON.stringify({
        interests: localIntent.interests,
        recipient: localIntent.recipient,
        occasion:  localIntent.occasion,
        budget:    localIntent.budget,
        age:       localIntent.age,
        groupSize: localIntent.groupSize,
        excluded:  localIntent.excluded
      }) + "\nCorrect it where it is wrong, confirm it where it is right.\n\n" +
      "Catalog (handle | title | price | collections):\n" + lines + "\n\n" +
      "Schema: {\"interests\":[\"handle\"],\"recipient\":\"handle-or-null\",\"occasion\":\"birthday|anniversary|halloween|holidays|mothers-day|christmas|wedding|null\"," +
      "\"budget\":number-or-null,\"age\":number-or-null,\"groupSize\":number-or-null,\"excluded\":[\"handle\"]," +
      "\"picks\":[{\"handle\":\"...\",\"reason\":\"4 words max\"}]}";

    return fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "x-api-key": aiCfg.apiKey,
        "anthropic-version": "2023-06-01",
        "content-type": "application/json",
        "anthropic-dangerous-direct-browser-access": "true"
      },
      body: JSON.stringify({
        model: aiCfg.model || "claude-haiku-4-5-20251001",
        max_tokens: 700,
        system: system,
        messages: [
          { role: "user", content: user },
          { role: "assistant", content: "{" }
        ]
      })
    })
      .then(function (r) {
        if (!r.ok) throw new Error("Anthropic API " + r.status);
        return r.json();
      })
      .then(function (data) {
        var text = data && data.content && data.content[0] && data.content[0].text;
        if (!text) throw new Error("Empty AI response");
        return JSON.parse("{" + text.trim().replace(/```$/, ""));
      });
  }

  /* Merge is deliberately conservative: the AI fills gaps and corrects
     categories, but it can never widen a budget or delete an exclusion the
     shopper typed in plain words. */
  function merge(local, ai) {
    if (!ai || typeof ai !== "object") return local;
    var out = local;

    if (Array.isArray(ai.interests) && ai.interests.length) {
      out.interests = ai.interests.filter(function (h) { return typeof h === "string"; }).slice(0, 3);
    }
    if (typeof ai.recipient === "string" && ai.recipient) out.recipient = ai.recipient;
    if (typeof ai.occasion === "string" && ai.occasion) out.occasion = ai.occasion;
    if (out.age == null && typeof ai.age === "number") out.age = ai.age;
    if (!out.groupSize && typeof ai.groupSize === "number") out.groupSize = ai.groupSize;

    /* Only tighten, never loosen. */
    if (typeof ai.budget === "number" && ai.budget >= 50) {
      out.budget = out.budget ? Math.min(out.budget, ai.budget) : ai.budget;
      out.budgetSource = out.budgetSource || "explicit";
    }

    if (Array.isArray(ai.excluded)) {
      ai.excluded.forEach(function (h) {
        if (typeof h === "string" && out.excluded.indexOf(h) === -1) out.excluded.push(h);
      });
    }
    out.interests = out.interests.filter(function (h) { return out.excluded.indexOf(h) === -1; });
    if (out.recipient && out.excluded.indexOf(out.recipient) !== -1) out.recipient = null;

    out.aiPicks = {};
    if (Array.isArray(ai.picks)) {
      ai.picks.forEach(function (pick) {
        if (pick && typeof pick.handle === "string") {
          out.aiPicks[pick.handle] = String(pick.reason || "").slice(0, 40);
        }
      });
    }
    return out;
  }

  /* ======================================================================== */
  /*  UI helpers                                                              */
  /* ======================================================================== */
  function collectRefs(root) {
    return {
      clear:     root.querySelector("[data-gf-clear]"),
      results:   root.querySelector("[data-gf-results]"),
      starters:  root.querySelector("[data-gf-starters]"),
      echo:      root.querySelector("[data-gf-echo]"),
      count:     root.querySelector("[data-gf-count]"),
      note:      root.querySelector("[data-gf-note-line]"),
      facets:    root.querySelector("[data-gf-facets]"),
      thinking:  root.querySelector("[data-gf-thinking]"),
      status:    root.querySelector("[data-gf-status]"),
      bar:       root.querySelector("[data-gf-bar]"),
      barFill:   root.querySelector("[data-gf-bar-fill]"),
      go:        root.querySelector("[data-gf-go]"),
      goIcon:    root.querySelector("[data-gf-btn-icon]"),
      goSpinner: root.querySelector("[data-gf-btn-spinner]"),
      more:      root.querySelector("[data-gf-more]"),
      moreArea:  root.querySelector("[data-gf-more-area]"),
      confetti:  root.querySelector("[data-gf-confetti]"),
      share:     root.querySelector("[data-gf-share]"),
      drawer:    root.querySelector("[data-gf-drawer]"),
      sticky:    root.querySelector("[data-gf-sticky]")
    };
  }

  function label(cfg, handle) {
    var labels = cfg.labels || {};
    if (labels[handle]) return decodeEntities(labels[handle]);
    return handle.replace(/-/g, " ").replace(/\b\w/g, function (m) { return m.toUpperCase(); });
  }

  function decodeEntities(s) {
    return String(s).replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
  }

  /* Renamed from rupees(): this store is not a rupee store, and a function
     named for one currency is how a hardcoded symbol grows back. MAJOR units. */
  function money(n) {
    return window.KitstersMoney.formatMajor(Math.round(n));
  }

  /* Real dispatch cut-off, computed in IST regardless of the visitor's clock. */
  function shipsToday() {
    var now = new Date();
    var ist = new Date(now.getTime() + (now.getTimezoneOffset() * 60000) + (5.5 * 3600000));
    var h = ist.getHours(), m = ist.getMinutes();
    if (h >= 14) return null;
    var minutes = (14 - h) * 60 - m;
    var hours = Math.floor(minutes / 60);
    var rest = minutes % 60;
    return "Order in " + hours + "h" + (rest ? " " + rest + "m" : "") + " - ships today";
  }

  function hasPhrase(text, phrase) {
    if (!phrase) return false;
    var re = new RegExp("(^|[^a-z0-9])" + escapeRe(String(phrase).toLowerCase()) + "([^a-z0-9]|$)", "i");
    return re.test(text);
  }
  function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
  function toNumber(s) { return parseFloat(String(s).replace(/,/g, "")) || 0; }

  function each(scope, selector, fn) {
    var nodes = scope.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) fn(nodes[i]);
  }

  function scrollTo(el) {
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: REDUCED ? "auto" : "smooth", block: "start" });
    }
  }

  function track(sentence, intent, outcome) {
    try {
      var payload = {
        event: "gift_finder_search",
        query: sentence,
        gf_interest:  (intent.interests || []).join(","),
        gf_recipient: intent.recipient || "",
        gf_occasion:  intent.occasion || "",
        gf_budget:    intent.budget || 0,
        gf_age:       intent.age || 0,
        gf_group:     intent.groupSize || 0,
        gf_excluded:  (intent.excluded || []).join(","),
        gf_mode:      outcome.mode,
        gf_relaxed:   outcome.relaxed || "",
        gf_results:   outcome.items.length
      };
      if (window.dataLayer && window.dataLayer.push) window.dataLayer.push(payload);
      if (typeof window.gtag === "function") window.gtag("event", "gift_finder_search", payload);
    } catch (e) { /* analytics must never break the search */ }
  }

  function initSticky(root, anchor, sticky) {
    if (!sticky || !anchor || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { sticky.classList.toggle("is-on", !entry.isIntersecting); });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  function initReveal(root) {
    var items = root.querySelectorAll(".kgf-reveal");
    if (!items.length) return;
    if (REDUCED || !("IntersectionObserver" in window)) return;
    root.classList.add("kgf-anim");
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-in");
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    for (var i = 0; i < items.length; i++) {
      if (items[i].getBoundingClientRect().top < (window.innerHeight || 800) * 0.9) {
        items[i].classList.add("is-in");
      } else {
        io.observe(items[i]);
      }
    }
  }

  /* ---------------------------------------------------------- the Deck --- */
  /* The hero's self-playing visual (owner, 2026-08-04, third design for this
     slot - see snippets/kitsters-gf-deck.liquid for why the gift box and the
     console were both dropped).

     A fanned deck of REAL product cards. Each cycle types the next sentence
     into the speech bubble, then rotates the deck so the matching card riffles
     to the front. The riffle is pure CSS: the only thing this writes is each
     card's `data-pos`, so the JS never touches geometry and never reads it
     back mid-animation.

     Bound from the BOOT block rather than from `init`, because `init` bails
     early when the results engine's catalog is missing - the hero must keep
     animating regardless of what else is on the page. */
  function initDeck(root) {
    var stage = root.querySelector("[data-gf-deck]");
    if (!stage) return;

    var scene = stage.querySelector("[data-gf-deck-scene]");
    var askEl = stage.querySelector("[data-gf-deck-ask]");
    var burst = stage.querySelector("[data-gf-deck-confetti]");
    var cards = stage.querySelectorAll("[data-gf-deck-card]");
    var steps = readJson(stage, "[data-gf-deck-data]", []);
    if (!askEl || !cards.length || !steps.length) return;

    /* A card is emitted per step in the same order, but never trust that the
       two lists are the same length - a step whose collection emptied is
       dropped from both, and a mismatch would pair a sentence with the wrong
       product, which is exactly the dishonesty this demo exists to avoid. */
    var COUNT = Math.min(steps.length, cards.length);
    var TYPE  = 38;
    var HOLD  = 3000;
    var RIFFLE = 700;

    var dkTimer = null;
    var dkIndex = 0;
    var dkLive  = false;

    function dkAt(ms, fn) { dkTimer = setTimeout(fn, ms); }

    /* Position 0 is the front of the fan; everything else falls in behind it
       in order, wrapping around. One attribute per card, CSS does the rest. */
    function dkArrange(front) {
      for (var c = 0; c < COUNT; c++) {
        cards[c].setAttribute("data-pos", String((c - front + COUNT) % COUNT));
      }
    }

    /* Reduced motion: hold step 1's finished pose. The information survives (a
       real sentence, a real match); only the riffle and the typing go. */
    if (REDUCED) {
      askEl.textContent = steps[0].q;
      dkArrange(0);
      return;
    }

    function dkPlay() {
      if (!dkLive) return;
      var i = dkIndex % COUNT;
      var text = String(steps[i].q || "");
      var n = 0;

      askEl.textContent = "";

      (function dkType() {
        if (!dkLive) return;
        askEl.textContent = text.slice(0, ++n);
        if (n < text.length) { dkAt(TYPE, dkType); return; }
        dkAt(240, dkRiffle);
      })();

      function dkRiffle() {
        if (!dkLive) return;
        dkArrange(i);
        confetti(burst);
        dkAt(RIFFLE + HOLD, dkNext);
      }

      function dkNext() {
        if (!dkLive) return;
        dkIndex = i + 1;
        dkPlay();
      }
    }

    function dkStart() { if (dkLive) return; dkLive = true; dkPlay(); }
    function dkStop()  { dkLive = false; clearTimeout(dkTimer); }

    /* Never animate offscreen - the loop would otherwise run for the life of
       the page, on a section most of the scroll depth never sees. */
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (entries[e].isIntersecting) dkStart(); else dkStop();
        }
      }, { threshold: 0.2 }).observe(stage);
    } else {
      dkStart();
    }

    document.addEventListener("visibilitychange", function () {
      if (document.hidden) dkStop();
      else if (stage.getBoundingClientRect().top < (window.innerHeight || 800)) dkStart();
    });

    /* Pointer parallax - the scene rotates as a whole and each sticker drifts
       by its own depth, which is what sells the 3-D fan. */
    if (scene) {
      var sats = scene.querySelectorAll(".kgf-deck-sat");

      stage.addEventListener("pointermove", function (e) {
        var r = stage.getBoundingClientRect();
        if (!r.width || !r.height) return;
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        scene.style.setProperty("--ry", (px * 12) + "deg");
        scene.style.setProperty("--rx", (-py * 9) + "deg");
        for (var i = 0; i < sats.length; i++) {
          var depth = parseFloat(sats[i].getAttribute("data-depth")) || 1;
          /* Keep the translateZ - the scene is `preserve-3d`, so dropping it
             here puts the sticker behind the deck as soon as you move. */
          sats[i].style.transform = "translateZ(7em) translate(" + (px * depth * 5) + "px," + (py * depth * 5) + "px)";
        }
      }, { passive: true });

      stage.addEventListener("pointerleave", function () {
        scene.style.setProperty("--ry", "0deg");
        scene.style.setProperty("--rx", "0deg");
        for (var i = 0; i < sats.length; i++) sats[i].style.transform = "";  /* back to the CSS translateZ */
      });
    }
  }

  /* Gift card: idle sway until you touch it, live tilt while you do, then it
     eases back and picks the idle animation up again. */
  function initGiftCard(root) {
    var card = root.querySelector("[data-gf-card]");
    if (!card || REDUCED) return;
    var settle = null;

    card.addEventListener("pointermove", function (e) {
      var r = card.getBoundingClientRect();
      if (!r.width) return;
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      clearTimeout(settle);
      card.classList.remove("is-idle");
      card.style.transition = "transform 100ms linear";
      card.style.transform = "perspective(1000px) rotateY(" + (px * 24) + "deg) rotateX(" +
        (-py * 18) + "deg) scale(1.04)";
    }, { passive: true });

    card.addEventListener("pointerleave", function () {
      card.style.transition = "transform var(--dur-slow) var(--ease-out-soft)";
      card.style.transform = "";
      clearTimeout(settle);
      settle = setTimeout(function () {
        if (!card.matches(":hover")) {
          card.classList.add("is-idle");
          card.style.transition = "";
          card.style.transform = "";
        }
      }, 640);
    });
  }

  function initPlaceholder(root, input, cfg) {
    var phrases = cfg.rotatePlaceholders || [];
    var field = root.querySelector("[data-gf-field]");
    if (!phrases.length || !field) return;

    if (REDUCED) { input.placeholder = phrases[0]; return; }

    var ghost = document.createElement("span");
    ghost.className = "kgf-placeholder";
    ghost.setAttribute("aria-hidden", "true");
    ghost.textContent = phrases[0];
    ghost.classList.add("is-in");
    field.appendChild(ghost);

    var index = 1, timer = null;

    function next() {
      if (document.activeElement === input || input.value) return;
      ghost.classList.remove("is-in");
      setTimeout(function () {
        ghost.textContent = phrases[index % phrases.length];
        index++;
        ghost.classList.add("is-in");
      }, 300);
    }
    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(function () { next(); schedule(); }, 3800);
    }
    schedule();

    input.addEventListener("focus", function () { ghost.classList.remove("is-in"); clearTimeout(timer); });
    input.addEventListener("blur", function () {
      if (!input.value) setTimeout(function () { if (!input.value) { next(); schedule(); } }, 500);
    });
    input.addEventListener("input", function () {
      if (input.value) { ghost.classList.remove("is-in"); clearTimeout(timer); }
    });
  }

  function confetti(host) {
    if (!host || REDUCED) return;
    var colors = ["#465ae6", "#ff4683", "#fdda0d", "#0a0a0a"];
    for (var i = 0; i < 28; i++) {
      (function (n) {
        var bit = document.createElement("i");
        bit.style.left = (50 + (Math.random() * 64 - 32)) + "%";
        bit.style.background = colors[n % colors.length];
        bit.style.borderRadius = Math.random() > 0.5 ? "50%" : "2px";
        host.appendChild(bit);
        var dx = Math.random() * 240 - 120;
        var dy = 110 + Math.random() * 160;
        var duration = 900 + Math.random() * 600;
        if (bit.animate) {
          bit.animate([
            { transform: "translate(0,0) rotate(0deg)", opacity: 1 },
            { transform: "translate(" + dx + "px," + dy + "px) rotate(" + (Math.random() * 420) + "deg)", opacity: 0 }
          ], { duration: duration, easing: "cubic-bezier(.2,.7,.3,1)", delay: Math.random() * 140 });
        }
        setTimeout(function () { if (bit.parentNode) bit.parentNode.removeChild(bit); }, duration + 300);
      })(i);
    }
  }
})();
